import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSearch, FiRefreshCw } from 'react-icons/fi';

const ProjectList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/projects', { params });
      setProjects(res.data);
    } catch (err) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Projects</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={fetchProjects} title="Refresh"><FiRefreshCw /></button>
          {isAdminHR && (
            <button className="btn-primary" onClick={() => navigate('/projects/new')}>
              <FiPlus /> New Project
            </button>
          )}
        </div>
      </div>

      <div className="filters-row">
        <div className="search-box">
          <FiSearch />
          <input placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Archived">Archived</option>
        </select>
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <table className="table">
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Key</th>
              <th>Project Name</th>
              <th>Lead</th>
              <th>Members</th>
              <th>Tasks</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p, index) => (
              <tr key={p._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${p._id}`)}>
                <td>{index + 1}</td>
                <td><span className="project-key-badge">{p.key}</span></td>
                <td><strong>{p.name}</strong></td>
                <td>{p.lead?.name || '-'}</td>
                <td>{p.members?.length || 0}</td>
                <td>{p.taskCount || 0}</td>
                <td>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${p.taskCount ? Math.round((p.doneCount / p.taskCount) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {p.taskCount ? Math.round((p.doneCount / p.taskCount) * 100) : 0}%
                  </span>
                </td>
                <td><span className={`status-badge status-${p.status.toLowerCase()}`}>{p.status}</span></td>
                <td className="actions" onClick={e => e.stopPropagation()}>
                  <button className="btn-sm btn-info" onClick={() => navigate(`/projects/${p._id}`)}><FiEye /></button>
                  {isAdminHR && (
                    <>
                      <button className="btn-sm btn-warning" onClick={() => navigate(`/projects/${p._id}/edit`)}><FiEdit /></button>
                      {user?.role === 'admin' && (
                        <button className="btn-sm btn-danger" onClick={() => handleDelete(p._id)}><FiTrash2 /></button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {projects.length === 0 && (
              <tr><td colSpan="9" style={{ textAlign: 'center' }}>No projects found</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProjectList;
